import React, { useRef } from "react";
import PropTypes from "prop-types";
import { DhlTextSrOnly } from "@dhl-official/react-library";
import { Icon } from "root/libs/ui-components/src";
import { keys } from "root/libs/constants/src";
import {
  StepperAndSummaryWrapper,
  StepperComponent,
  StepperWrapper,
  StepperContent,
  StepperSteps,
  StepContainer,
  StepCircle,
  StepLabel,
  StepConnector,
  IllustrationContainer,
  CornerIconWrapper,
  CornerNumberCircle,
  StepperSummaryWrapper,
  ShipmentSummaryContainer,
  SummaryIconWrapper,
  SummaryContent,
  SummaryTitle,
  SummaryDetails,
  DetailSection,
  DetailLabel,
  DetailValue,
  ProductListWrapper,
} from "./styled";

const StepperCard = ({
  steps,
  currentStep,
  ariaDescription,
  getStepStatus,
  getIllustration,
  stepNumber,
  currentPage,
  summary,
  getCopy,
  COPY_ID_PREFIX_SHIPMENT_SUMMARY,
  getCountryValue,
  importToggleEnabled,
  originFields,
  destinationFields,
  productItems,
  formatProductDetails,
  checkIcon,
  moveToStep,
}) => {
  const inputRef = useRef([]);

  const handleKeyUp = (e) => {
    const { code } = e;
    if (code === keys.SPACE || code === keys.RETURN) {
      moveToStep(1); // Move to Tradelane
    }
  };

  return (
    <StepperAndSummaryWrapper>
      <StepperComponent>
        <StepperWrapper>
          <StepperContent>
            <StepperSteps>
              {steps.map((step, index) => {
                const status = getStepStatus(step.id);
                const isLast = index === steps.length - 1;
                const stepIndex = index + 1;
                return (
                  <StepContainer
                    key={step.id}
                    tabIndex={status === "completed" ? 0 : -1}
                    onKeyDown={(e) => {
                      const { code } = e;
                      if (code === keys.SPACE || code === keys.RETURN) {
                        moveToStep(stepIndex);
                      }
                    }}
                  >
                    <StepCircle
                      status={status}
                      onClick={() => {
                        if (status === "completed") {
                          moveToStep(stepIndex);
                        }
                      }}
                    >
                      {status === "completed" ? (
                        <Icon
                          icon={checkIcon}
                          useAs="decorative"
                          width={{ mobile: "16px", desktop: "16px" }}
                        />
                      ) : (
                        step.id
                      )}
                    </StepCircle>
                    <StepLabel
                      ref={(el) => {
                        inputRef.current[index] = el;
                      }}
                      status={status}
                      id={`step-${stepIndex}-btn`}
                      data-step={stepIndex}
                      data-testid={stepIndex}
                      onClick={() => {
                        if (status === "completed") {
                          moveToStep(stepIndex);
                        }
                      }}
                    >
                      {step.label}
                    </StepLabel>
                    {status === "active" && (
                      <DhlTextSrOnly>
                        {`Step ${currentStep} of ${steps?.length},`}
                      </DhlTextSrOnly>
                    )}
                    {status === "completed" && (
                      <DhlTextSrOnly>Completed: </DhlTextSrOnly>
                    )}
                    {status === "active" && (
                      <DhlTextSrOnly>
                        {ariaDescription[currentStep]}
                      </DhlTextSrOnly>
                    )}
                    {!isLast && (
                      <StepConnector completed={status === "completed"} />
                    )}
                  </StepContainer>
                );
              })}
            </StepperSteps>

            <IllustrationContainer currentStep={currentStep}>
              <Icon
                icon={getIllustration()}
                useAs="decorative"
                width={{ mobile: "24px", desktop: "350px" }}
              />
              <CornerIconWrapper>
                <Icon
                  icon={stepNumber}
                  useAs="decorative"
                  width={{ mobile: "24px", desktop: "180px" }}
                />
                <CornerNumberCircle>{currentStep}</CornerNumberCircle>
              </CornerIconWrapper>
            </IllustrationContainer>
          </StepperContent>
        </StepperWrapper>
      </StepperComponent>

      {currentPage !== "TradeLane" && (
        <StepperSummaryWrapper>
          <ShipmentSummaryContainer>
            <SummaryIconWrapper>
              <Icon
                icon={summary}
                useAs="decorative"
                width={{ mobile: "40px", desktop: "40px" }}
              />
            </SummaryIconWrapper>

            <SummaryContent>
              <SummaryTitle>
                {getCopy(
                  "headline",
                  undefined,
                  COPY_ID_PREFIX_SHIPMENT_SUMMARY
                )}
              </SummaryTitle>

              <SummaryDetails>
                <DetailSection>
                  <DetailLabel>
                    {getCopy(
                      "from",
                      undefined,
                      COPY_ID_PREFIX_SHIPMENT_SUMMARY
                    )}
                  </DetailLabel>
                  <DetailValue
                    tabIndex={0}
                    onClick={() => {
                      moveToStep(1); // Move to Tradelane
                    }}
                    onKeyUp={handleKeyUp}
                  >
                    {getCountryValue(
                      !importToggleEnabled
                        ? originFields.fields.country
                        : destinationFields.fields.country
                    )}
                  </DetailValue>
                </DetailSection>

                <DetailSection>
                  <DetailLabel>
                    {getCopy("to", undefined, COPY_ID_PREFIX_SHIPMENT_SUMMARY)}
                  </DetailLabel>
                  <DetailValue
                    tabIndex={0}
                    onClick={() => {
                      moveToStep(1); // Move to Tradelane
                    }}
                    onKeyUp={handleKeyUp}
                  >
                    {getCountryValue(
                      importToggleEnabled
                        ? originFields.fields.country
                        : destinationFields.fields.country
                    )}
                  </DetailValue>
                </DetailSection>
              </SummaryDetails>
            </SummaryContent>
          </ShipmentSummaryContainer>

          <ProductListWrapper>
            {[...productItems].map((product, index) =>
              formatProductDetails(product, index)
            )}
          </ProductListWrapper>
        </StepperSummaryWrapper>
      )}
    </StepperAndSummaryWrapper>
  );
};

StepperCard.propTypes = {
  steps: PropTypes.array.isRequired,
  currentStep: PropTypes.number.isRequired,
  ariaDescription: PropTypes.object.isRequired,
  getStepStatus: PropTypes.func.isRequired,
  getIllustration: PropTypes.func.isRequired,
  stepNumber: PropTypes.any.isRequired,
  currentPage: PropTypes.string.isRequired,
  summary: PropTypes.any.isRequired,
  getCopy: PropTypes.func.isRequired,
  COPY_ID_PREFIX_SHIPMENT_SUMMARY: PropTypes.string.isRequired,
  getCountryValue: PropTypes.func.isRequired,
  importToggleEnabled: PropTypes.bool.isRequired,
  originFields: PropTypes.object.isRequired,
  destinationFields: PropTypes.object.isRequired,
  productItems: PropTypes.array.isRequired,
  formatProductDetails: PropTypes.func.isRequired,
  checkIcon: PropTypes.any.isRequired,
  moveToStep: PropTypes.func.isRequired,
};

export default StepperCard;
